/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { LazyImageStyle } from "@insite/mobius/LazyImage";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { NotifyProps } from "@monsma/XDL/Components/Notify";
import { hideScrollBar, removeLinkStyle } from "@monsma/XDL/Utilities/sharedStyles";
import { css } from "styled-components";

export interface MiniCartOverlayStyles {
    miniCartContainerStyles?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    miniCartHeadingStyles?: InjectableCss;
    notificationContainers?: NotifyProps;
    miniCartHeadingWrapperStyles?: InjectableCss;
    fancyOverflowStyles?: InjectableCss;
    fancyWrapperStyles?: InjectableCss;
    cartLineStyles?: InjectableCss;
    cartLineMainSectionStyles?: InjectableCss;
    cartLineRemoveLinkStyles?: LinkPresentationProps;
    cartLineImageStyles?: InjectableCss;
    cartLineDescriptionNumbersWrapper?: InjectableCss;
    cartLineErrorMsgsStyles?: InjectableCss;
    cartLineQtyPriceSectionStylesWrapper?: InjectableCss;
    cartLineQtyWrapper?: InjectableCss;
    cartLinePriceWrapper?: InjectableCss;
    minicartActionButtonsStyles?: InjectableCss;
    minicartCheckoutButton?: InjectableCss;
    cartLineQtyStyle?: CartLineQuantityStyles;
    cartLineRemoveSectionWrapper?: InjectableCss;
    minicartSubTotal?: InjectableCss;
    productImageStyles?: ProductImageStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    productPrice?: ProductPriceStyles;
}

export const minicartStyles: MiniCartOverlayStyles = {
    miniCartContainerStyles: {
        css: css`
            width: 536px;
            max-height: 780px;
            min-height: 200px;
            overflow: hidden;
            display: flex;
            flex-direction: column;
        `,
    },
    miniCartHeadingStyles: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },

    miniCartHeadingWrapperStyles: {
        css: css`
            padding-bottom: 22px;

            & > ${TypographyStyle} {
                font-size: 16px;
                line-height: 20px;
                color: ${getColor("common.ui10")};
            }
        `,
    },
    fancyWrapperStyles: {
        css: css`
            position: relative;

            margin-left: -20px;
            margin-right: -20px;
        `,
    },

    fancyOverflowStyles: {
        css: css`
            max-height: 500px;
            min-height: 108px;
            overflow-y: auto;
            overflow-x: hidden;
            ${hideScrollBar};

            @media (max-height: 800px) {
                max-height: 200px;
            }
        `,
    },
    spinnerWrapper: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;

            &:before {
                content: "";
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background: ${getColor("primary.contrast")};
                opacity: 0.75;
                z-index: ${getProp("theme.zIndex.default")};
            }

            & ${/* sc-selector */ LoadingSpinnerStyle} {
                z-index: ${getProp("theme.zIndex.level2")};
            }
        `,
    },
    cartLineStyles: {
        css: css`
            width: 100%;
            padding: 15px 20px;
            display: flex;
            border: 0;
            position: relative;
            // border-bottom: 1px solid ${getColor("common.border")};
            transition: all ease-in-out 200ms;

            &:first-child {
                padding-top: 0;
            }
            &:last-child {
                border-bottom: 0;
            }
            &:hover {
                background-color: ${getColor("common.ui7")};
                border-radius: 5px;
            }
        `,
    },
    cartLineMainSectionStyles: {
        css: css`
            display: block;
            width: 100%;
        `,
    },

    cartLineImageStyles: {
        css: css`
            margin-right: 10px;
            border: 1px solid ${getColor("common.ui12")};
            border-radius: 4px;
            background-color: ${getColor("common.background")};
            cursor: pointer;
            display: flex;
            align-items: center;
            padding: 0px;
            text-align: unset;
            text-decoration: none;
            margin-right: 20px;
            height: 72px;
            width: 72px;
            padding: 1px;

            > a {
                height: 100%;
                justify-content: center;
            }

            ${/* sc-selector */ LazyImageStyle} {
                position: relative;
                display: inline-flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                overflow: hidden;                
            }

            & img {
                object-fit: contain;
                object-position: center;
            }
        `,
    },
    cartLineDescriptionNumbersWrapper: {
        css: css`
            overflow-wrap: break-word;
            word-wrap: break-word;
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            typographyProps: {
                size: 14,
                lineHeight: "19px",
                transform: "uppercase",
                ellipsis: true,
            },
        },
        productDetailDescription: { size: 14, lineHeight: "19px", transform: "uppercase", ellipsis: true },
    },
    cartLineErrorMsgsStyles: {
        css: css`
            width: 100%;
            padding-top: 10px;
            display: flex;
            justify-content: flex-end;
            flex-direction: column;

            & .error-message {
                margin-top: 5px;
                font-size: 13px;
                text-align: right;
            }
        `,
    },
    cartLineQtyPriceSectionStylesWrapper: {
        css: css`
            display: inline-flex;
            align-items: center;
            margin-top: 20px;
        `,
    },
    cartLineQtyWrapper: {
        css: css`
            margin-right: 30px;
        `,
    },

    productPrice: {
        price: {
            priceText: {
                css: css`
                    font-size: 16px;
                    line-height: 20px;
                    color: ${getColor("common.ui10")};
                    font-weight: 700;
                `,
            },
            errorText: {
                css: css`
                    line-height: 20px;
                    color: ${getColor("common.ui10")};
                `,
            },
            unitOfMeasureText: {
                css: css`
                    color: ${getColor("text.main")};
                    font-size: 13px;
                    line-height: 22px;
                `,
            },
        },
        quoteMessage: {
            text: {
                css: css`
                    margin-right: 5px;
                `,
            },
        },
    },
    minicartActionButtonsStyles: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            background-color: ${getColor("common.background")};
            border-top: 1px solid ${getColor("common.border")};
            padding: 20px 0 0 0;
        `,
    },
    cartLineQtyStyle: {
        editableQuantityTextField: {
            sizeVariant: "small",
            cssOverrides: {
                formInputWrapper: css`
                    ${getProp("theme.formField.defaultProps.cssOverrides.formInputWrapper")}
                    width: 65px;

                    &&&& {
                        margin: 0;
                    }
                `,
                inputSelect: css`
                    ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                    background: ${getColor("common.ui8")};
                    font-size: 14px;
                `,
            },
            labelProps: {
                css: css`
                    display: none;
                `,
            },
        },
    },

    cartLineRemoveSectionWrapper: {
        css: css`
            position: absolute;
            top: 15px;
            right: 20px;
        `,
    },
    cartLineRemoveLinkStyles: removeLinkStyle,
    minicartSubTotal: {
        css: css`
            display: flex;
            justify-content: flex-end;

            & span {
                font-size: 15px;
                letter-spacing: 0;
                line-height: 26px;
                margin-right: 10px;
            }

            & strong {
                font-size: 20px;
                letter-spacing: 0;
                line-height: 26px;
            }
        `,
    },
    minicartCheckoutButton: {
        css: css`
          cursor: pointer;
          line-height: 1;
          transition: all 200ms ease-in-out 0s;
          word-break: keep-all;
          padding: 0 30px;
          background: ${getColor("common.ui2")};
          border: 1px solid ${getColor("common.ui2")};
          color: ${getColor("common.background")};
          display: inline-flex;
          height: 46px;
          backface-visibility: visible;
          border-radius: 4px;
          
          &:hover {
            background: ${getColor("common.ui3")};
            border-color: ${getColor("common.ui3")};
          }
        `
    }
};
